/**
 * PssWidget — Predictive Safety Score. Calls the existing legacy endpoint
 * (/dashboard/pss) directly rather than reimplementing the weighted score, so
 * there is no divergence from the executive/landing pages.
 */
import React from 'react';
import axios from 'axios';
import { useDashboard } from '../context/DashboardContext';
import { useDashboardData } from '../hooks/useDashboardData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK = {
  'Low Risk': 'text-green-700 bg-green-50 border-green-200',
  'Emerging Risk': 'text-amber-700 bg-amber-50 border-amber-200',
  'Potentially Serious': 'text-red-700 bg-red-50 border-red-200',
};

export default function PssWidget({ onClick }) {
  const { siteId, refreshMs } = useDashboard();
  const { data, loading, error } = useDashboardData(
    (signal) => {
      const p = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
      return axios.get(`${API}/dashboard/pss${p}`, { signal }).then((r) => r.data);
    },
    [siteId],
    { refreshMs },
  );

  const score = data?.score;
  const risk = data?.risk_level;
  const riskCls = RISK[risk] || 'text-gray-700 bg-gray-50 border-gray-200';
  const clickable = typeof onClick === 'function';

  return (
    <div
      className={`rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md ${clickable ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400/60' : ''}`}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick(e) : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-purple-500" />
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Predictive Safety Score</h3>
      </div>
      {loading && <div className="h-16 animate-pulse rounded bg-gray-100" />}
      {!loading && error && <div className="text-sm text-red-600">Unavailable</div>}
      {!loading && !error && (
        <div className="mt-2">
          <div className="flex items-baseline gap-1.5">
            <span className="text-3xl font-semibold tabular-nums text-purple-700">{score ?? '—'}</span>
            {score != null && <span className="text-sm text-gray-400">/ 100</span>}
          </div>
          {risk && (
            <span className={`mt-2 inline-block rounded border px-2 py-0.5 text-xs font-medium ${riskCls}`}>{risk}</span>
          )}
          {data?.message && <p className="mt-2 text-xs text-gray-500">{data.message}</p>}
        </div>
      )}
    </div>
  );
}
